/**
 * SiteContacts — route child at `/project/:id/contacts`, nested under the
 * ProjectSummary layout (#149). The page-level header ("Site Contacts" +
 * "Back to Site Overview" breadcrumb) was removed when the layout started
 * rendering the site name above — the persistent KPI bar identifies the
 * site, and the tab row labels this tab "Contacts".
 *
 * Per-site contact directory. Accessible to all authenticated users with
 * access to the site. Admin-only inline edit actions (gated on
 * ADMIN_ROLES); backend also enforces via requireMinimumRole.
 *
 * Issue: djin-cloud#64
 */

import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ADMIN_ROLES, type Role } from '../constants/roles';
import { useAuthStore } from '../store/authStore';
import { fetchSiteContacts } from '../services/adminApi';
import { SiteContactsTable } from '../components/site-detail/SiteContactsTable';
import { SiteDetailTabs } from '../components/site-detail/SiteDetailTabs';
import { useProjectSummarySite } from '../hooks/useProjectSummarySite';

export function SiteContacts() {
  const { id = '' } = useParams<{ id: string }>();
  const site = useProjectSummarySite();
  const userRole = useAuthStore((s) => s.user?.role) as Role | undefined;
  const canEdit = !!userRole && ADMIN_ROLES.includes(userRole);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['site-contacts', id],
    queryFn: () => fetchSiteContacts(id),
    enabled: !!id,
  });

  return (
    <div className="flex flex-col flex-1 min-h-0" data-testid="site-contacts-page">
      <SiteDetailTabs siteId={id} site={site} />

      <div className="space-y-4 p-6">
        {isLoading && (
          <div className="p-6 text-sm text-ems-text-muted">Loading contacts…</div>
        )}
        {isError && (
          <div className="p-6 text-sm text-ems-red">Failed to load contacts.</div>
        )}
        {data && <SiteContactsTable siteId={id} contacts={data} canEdit={canEdit} />}
      </div>
    </div>
  );
}
